import {Owner, OwnerInfo, Permissions} from 'auth/PermissionConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {TagSchema} from 'constants/RegistryConstants';
import {BatchSummary} from 'constants/SampleConstants';
import {ProjectSummary, SourceSummary} from 'constants/SourceConstants';
import {User} from 'constants/UserConstants';
import * as FileConstants from 'folder-items/FileConstants';
import {StructuredData} from 'notebook/data-tables/DataTableConstants';
import {EntryDaySummary, EntryVersion, FromServerId, ServerEntryVersion} from 'notebook/ELNConstants';
import {Review} from 'notebook/reviews/ReviewConstants';
import {TransferOperation, TransferOperationList} from 'notebook/transfer-tables/TransferTableConstants';
import {SampleLinkSchemaField} from 'schemas/fields/SchemaFieldConstants';
import {AnonymousSchema} from 'schemas/SchemaConstants';
import {TagUpdateObject} from 'schemas/tags/TagUtil';
import {Optional} from 'util/Types';
import {STAGE_RUN_STATUS} from 'workflows/constants/StageRunConstants';

import {FolderSummary} from '../constants/FolderConstants';

type Tag = FileConstants.Tag;

export const FILE_TYPES_WITH_MODAL_EDITOR = [ModelTypeConstants.STAGE_CONFIGURATION];

export const WORKFLOW_STATUS_DRAFT = 'DRAFT'; // Keep in sync with workflow.py

export const CONTAINER_COLUMN_LABEL = 'Container';

export const SAMPLE_COLUMN_LABEL = 'Sample ID';

export const STAGE_RUN_COLUMN_LABEL = 'Stage Run';

export enum WorkflowScreen {
  WORKFLOW_DASHBOARD = 'WORKFLOW_DASHBOARD',
  WORKFLOW_DASHBOARD_TEMPLATES = 'WORKFLOW_DASHBOARD_TEMPLATES',
  WORKFLOW_DETAILS = 'WORKFLOW_DETAILS',
  WORKFLOW_TEMPLATE_VIEW = 'WORKFLOW_TEMPLATE_VIEW',
}

export const WORKFLOW_SCREEN = {
  WORKFLOW_DASHBOARD: 'WORKFLOW_DASHBOARD',
  WORKFLOW_DASHBOARD_TEMPLATES: 'WORKFLOW_DASHBOARD_TEMPLATES',
  WORKFLOW_DETAILS: 'WORKFLOW_DETAILS',
  WORKFLOW_TEMPLATE_VIEW: 'WORKFLOW_TEMPLATE_VIEW',
};

export const WORKFLOW_FILTER_ANY = 'ANY';

export const WORKFLOW_FILTER_NONE = 'NONE';

export const WORKFLOW_STATUSES = [
  WORKFLOW_STATUS_DRAFT,
  STAGE_RUN_STATUS.INITIALIZED,
  STAGE_RUN_STATUS.COMPLETED,
];

export const WORKFLOW_STATUS_DISPLAY_NAMES: {[status: string]: string} = {
  [STAGE_RUN_STATUS.INITIALIZED]: 'In progress',
  [STAGE_RUN_STATUS.COMPLETED]: 'Completed',
  [WORKFLOW_STATUS_DRAFT]: 'Draft',
};

// TODO(TypeScript): rename to CamelCase
export enum INPUT_TABLE_SORT_BY_TYPE {
  DEFAULT = 'DEFAULT',
  SAMPLE = 'SAMPLE',
  CONTAINER = 'CONTAINER',
  STAGE_RUN = 'STAGE_RUN',
  EXPERIMENTAL_CONDITION_VALUE = 'EXPERIMENTAL_CONDITION_VALUE',
}

// Note that when in configuration mode the screen is not collapsible,
// so we have to manually expand it.
export const COLLAPSIBLE_WORKFLOW_SCREENS = [WORKFLOW_SCREEN.WORKFLOW_DETAILS];

export interface StageRunBioentity {
  id: string;
  displayLabel: string;
}
export interface StageRunSample {
  // New samples don't have an id
  // TODO(TypeScript): handle cases where id is null
  id: string;
  name: string;
  bioentity?: Optional<StageRunBioentity>;
  // If the user doesn't have permissions for the batch or it was deleted, batch and url are undefined.
  batch?: Optional<BatchSummary>;
  fileId?: string | null;
  containerId?: string | null;
  url: string | null;
}

export interface StageEntrySummary {
  id: string;
  editURL: string;
  created_at: string;
  humanId: string | null;
  modified_at: string;
  name: string;
  permissions: Permissions;
  entryDays: Array<EntryDaySummary>;
  tags: Array<Tag>;
  review?: Review;
}

export interface WorkflowTemplateSummary {
  id: string;
  name: string;
}

export interface ExperimentalConditionSummary {
  id: string;
  name: string;
}

export interface ExperimentalConditionValue {
  // Null value indicates condition value has not been saved
  id?: string;
  name: string;
  textValue: string;
  // Null value indicates condition value has not been saved
  experimentalCondition?: ExperimentalConditionSummary;
}

export interface ExperimentalCondition {
  // Null value indicates condition has not been saved
  id: string | null;
  name: string;
  values: Array<ExperimentalConditionValue>;
}

export interface StageConfigurationSummary {
  id: string;
  entryTemplateName: string;
  stageName: string;
  index: number;
}

export interface StageFlowNodeSummary {
  id: string;
}

export interface WorkflowTemplateVersionSummary {
  id: string;
  description: string | null;
  modifiedAt: string;
  version: number | null;
  workflowTemplate: WorkflowTemplateSummary | null;
}

export interface StageConfiguration extends StageConfigurationSummary {
  experimentalConditions: Array<ExperimentalCondition>;
  stageFlowNodes: Array<StageFlowNodeSummary>;
  workflowTemplateVersion: WorkflowTemplateVersionSummary;
  permissions: Permissions;
  tags: Array<Tag>;
  tagSchema: TagSchema | null;
}

export interface WorkflowStageSummary {
  id: string;
  name: string;
}

export interface WorkflowSummary {
  id: string;
  name: string;
  humanId: string;
  source: ProjectSummary;
  folder?: FolderSummary | null;
  description: string | null;
  createdAt: string;
  modifiedAt: string;
  authors: Array<User>;
  editURL: string;
}

export interface RegistrationRecord {
  id: string;
  stageRunId: string;
  stageRunSampleId: string;
}

export interface RegistrationRecordList {
  id: string;
  registrationRecords: Array<RegistrationRecord>;
}

export interface InputTableConfig {
  sortByType: INPUT_TABLE_SORT_BY_TYPE;
  sortByExperimentalConditionId: string | null;
  isSortReversed: boolean | null;
}

export interface ServerStageEntry extends StageEntrySummary {
  stageRuns: Array<StageRunSummary>;
  workflowId: string;
  workflowStageId: string;
  inputTableConfig: InputTableConfig;
  review?: Review;
  tags: Array<FileConstants.SchemaTag>;
  tagSchema: TagSchema | null;
  source: SourceSummary;
  authors: Array<User>;
}

export interface ServerStageEntryDocument extends ServerStageEntry {
  entryVersion: ServerEntryVersion<FromServerId>;
  latestVersionId: number;
}

// Client-side Stage Entry - has cid but not id
export interface StageEntryDocument extends ServerStageEntry {
  latestVersionId: number;
  entryVersion: EntryVersion;
}

export interface WorkflowTemplate extends WorkflowTemplateSummary {
  workflowTemplateVersions: Array<WorkflowTemplateVersionSummary>;
  permissions: Permissions;
  prefix: string;
  owner: Owner;
  ownerInfo: OwnerInfo;
}

export interface WorkflowStage {
  id: string;
  name: string;
  stageConfiguration: StageConfigurationSummary;
  stageEntries: Array<StageEntrySummary>;
  permissions: Permissions;
}

export interface StageFlowNode extends StageFlowNodeSummary {
  parentIds: Array<string>;
  experimentalConditionValues: Array<ExperimentalConditionValue>;
}

export interface StageRunSummary {
  createdAt: string;
  id: string;
  name: string;
}

export interface StageRun {
  createdAt: string;
  id: string;
  name: string;
  status: STAGE_RUN_STATUS;
  inputSampleIds: Array<string>;
  outputSampleIds: Array<string>;
  stageEntry: StageEntrySummary;
  // TODO(ala) DEPRECATED - don't use!
  parent?: StageRunSummary | null;
  stageFlowNode: StageFlowNode;
}

export interface WorkflowTemplateVersion extends WorkflowTemplateVersionSummary {
  stageConfigurations: Array<StageConfigurationSummary>;
  permissions: Permissions;
}

export interface InitializationDataSummary {
  id: string;
  requestFulfillmentId: string | null;
}

export interface Workflow extends WorkflowSummary {
  baseWorkflowTemplateVersionId: string | null;
  initializationDatas: Array<InitializationDataSummary>;
  workflowTemplateVersion: WorkflowTemplateVersionSummary;
  workflowStages: Array<WorkflowStageSummary>;
  permissions: Permissions;
}

export interface WorkflowStructuredDataRow {
  stageRunId: string;
  stageRunSampleId: string;
  fields: Array<{
    tableName: string;
    name: string;
    value: string | null;
    important: boolean;
  }>;
}

export enum WorkflowStatus {
  INITIALIZED = 'INITIALIZED',
  COMPLETED = 'COMPLETED',
  DRAFT = 'DRAFT',
}

export interface WorkflowDashboardDetails {
  status: WorkflowStatus;
  lastStageCompletionChange: {
    stageName: string;
    lastStageCompletedAt: Optional<string>;
  };
}

export type WorkflowStructuredData = Array<WorkflowStructuredDataRow>;

export interface StageRunSampleKey {
  stageRunId: string;
  stageRunSampleId: string;
}

export interface StageRunSampleDetails {
  stageRun: StageRun;
  stageRunSample: StageRunSample;
}

export interface StageEntrySummaryData {
  hasImportant: boolean;
}

// Data sources
export const INPUT_SAMPLES_SOURCE_UUID = 'INPUT_SAMPLES_SOURCE_UUID';
export const INPUT_SAMPLES_SOURCE_NAME = 'Input samples';

// TODO: Move SampleGroup related constants out of WorkflowConstants (they're now considered samples models,
// not workflows models).

export interface SampleGroupSchema extends AnonymousSchema {
  allFields: Array<SampleLinkSchemaField>;
  fields: Array<SampleLinkSchemaField>;
}

export interface BaseSampleGroup {
  tags: Array<FileConstants.SchemaTag>;
}

export interface SampleGroup extends BaseSampleGroup {
  id: string;
}

export interface UISampleGroup extends BaseSampleGroup {
  id?: string;
  cid: string;
}

export interface SampleGroupUpdate {
  cid: string;
  tags: TagUpdateObject;
}

// Keep in sync with workflow_details.py:get_workflow_template_version_details_dict()
export interface WorkflowTemplateVersionDetails {
  workflowTemplateVersion: WorkflowTemplateVersion;
  stageConfigurations: {[stageConfigId: string]: StageConfiguration};
  stageFlowNodes: {[stageFlowNodeId: string]: StageFlowNode};
}

// Keep in sync with workflow_details.py:get_workflow_details_dict()
export interface WorkflowDetails extends WorkflowTemplateVersionDetails {
  workflow: Workflow;
  workflowStages: {[stageId: string]: WorkflowStage};
  stageEntries: {[stageEntryId: string]: ServerStageEntry};
  stageRuns: {[stageRunId: string]: StageRun};
  entryIdToOrderedInputSampleKeys: {[stageEntryId: string]: Array<StageRunSampleKey>};
  entryIdToOrderedOutputSampleKeys: {[stageEntryId: string]: Array<StageRunSampleKey>};
  // TODO(ala): entryIdToStructuredData is temporarily optional until we remove it entirely
  entryIdToStructuredData?: {[stageEntryId: string]: StructuredData};
  // TODO(ala): entryIdToSummaryData is temporarily optional until we remove it entirely
  entryIdToSummaryData?: {[stageEntryId: string]: {hasImportant: boolean}};
}

// Keep in sync with workflow_details.py:get_stage_entry_details_dict()
export interface StageEntryDetails {
  stageEntry: StageEntryDocument;
  stageRuns: {[stageRunId: string]: StageRun};
  stageFlowNodes: {[stageFlowNodeId: string]: StageFlowNode};
  registrationRecordListsById: {[registrationRecordListId: string]: RegistrationRecordList};
  transferOperationListsById: {[transferOperationListId: string]: TransferOperationList};
  transferOperationsById: {[transferOperationId: string]: TransferOperation};
  inputSamplesOrder: Array<StageRunSampleKey>;
  outputSamplesOrder: Array<StageRunSampleKey>;
  workflowStageOrderedStageRunIds: Array<string>;
}

export const WorkflowPreferences = {
  EXTERNAL_INPUT_PICKER_INPUT_TYPE: 'workflows.externalInputPicker.inputType',
  REGISTRATION_TABLE_SAMPLE_TYPE: 'workflows.registrationTable.sampleType',
  SELECT_PARENT_GRID_MODAL_SELECTED_CREATE_TYPE: 'workflows.selectParentGridModal.selectedCreateType',
  SELECT_PARENT_GRID_MODAL_SELECTED_DESTINATION: 'workflows.selectParentGridModal.selectedDestination',
};
